// reports.mjs — 汇报读面的纯逻辑（第一百零二批，SB-106「页面呈现」）。无 React、无 DOM。
//
// 三件事离开 JSX 才有判据（房屋风格：pages/docHistory.mjs 同款）：
//
// 1) 列表规整。汇报是 agent 经 dck_ key 写进来的，写入侧不是这个前端——也就是说
//    这些行的形状由**别人**决定。缺 reportKey 的行在页面上是一颗哑弹（点开必然
//    404，因为 key 就是取正文的那把钥匙），所以在进入渲染之前就滤掉，而不是让它
//    在列表里占一行等着被点。
// 2) 选中项的收敛。切组即换数据，而"选中的那篇"是攥在组件手里的字符串——旧 key
//    在新组里通常不存在。这里做成**派生**而不是 effect 里 setSelected(null)：
//    docHistory.mjs 已经为同一个形状付过学费（同一次提交里，effect 的 setState
//    对紧接着跑的下一个 effect 不可见，于是会拿 (新组, 旧 key) 白跑一发请求）。
// 3) 修订列表的顺序。后端是 `ORDER BY revision DESC`，页面要的也是"最近的在最前"。
//    这里显式排一次不是不信任后端，而是让这条**显示语义**有一个本地判据——否则
//    它唯一的依据是远端一句 SQL，谁改了那句都没有任何东西会响。

// 列表行 → 受控形状。title 缺失时回落到 reportKey：一篇没有标题的汇报仍然要能被
// 点开（key 本身就是人写的、可读的），显示一个空行等于把它藏起来。
export function normalizeReports(data) {
  const rows = Array.isArray(data?.reports) ? data.reports : [];
  const out = [];
  for (const r of rows) {
    if (!r || typeof r !== 'object') continue;
    const key = typeof r.reportKey === 'string' ? r.reportKey.trim() : '';
    if (!key) continue;                       // 哑弹：没有 key 就取不到正文
    const title = typeof r.title === 'string' && r.title.trim() ? r.title.trim() : key;
    out.push({
      reportKey: key,
      title,
      bodyFormat: typeof r.bodyFormat === 'string' && r.bodyFormat ? r.bodyFormat : 'markdown',
      lang: typeof r.lang === 'string' ? r.lang : '',
      revision: Math.max(0, Math.trunc(Number(r.revision)) || 0),
      viaAgent: r.viaAgent === true,          // 只有真 true 才算 agent 写的
      updatedAtMs: Number.isFinite(Number(r.updatedAtMs)) ? Number(r.updatedAtMs) : 0,
    });
  }
  // 刻意**不排序**：后端是 `ORDER BY report_key ASC`，在这里再排一次就是第二个
  // 会漂的顺序（同一批汇报在两次改动之间换了位置，而没有任何东西解释为什么）。
  return out;
}

// 当前该展示哪一篇。wanted 仍在列表里就留住（换语言、重载列表不该把用户踢回第一篇），
// 否则落到第一篇；空列表返回空串（调用侧据此走"该组无汇报"的空态）。
export function pickReportKey(reports, wanted) {
  const list = Array.isArray(reports) ? reports : [];
  if (list.length === 0) return '';
  const w = typeof wanted === 'string' ? wanted : '';
  if (w && list.some((r) => r.reportKey === w)) return w;
  return list[0].reportKey;
}

// ── 第三百零九批（SB-202 S3 余项）：按 key 前缀分「标签」看清单 ─────────────────
// 汇报 key 的第一段就是它的「标签」（契约：`progress-report/<日期>` 的前缀即标签，不另建标签表）。
// 资方三日简报每三天一篇，几周后就把别的汇报挤出首屏——所以清单上给一排标签片，点一枚只看那一族。
// 无斜杠的 key 归「其它」（空串标签）。
export function tagOf(reportKey) {
  const k = typeof reportKey === 'string' ? reportKey : '';
  const i = k.indexOf('/');
  return i > 0 ? k.slice(0, i) : '';
}

// 清单里出现过的标签，按首次出现的顺序（不排序：顺序跟着后端的 key 序走，理由同 normalizeReports）。
export function tagsOf(reports) {
  const seen = [];
  for (const r of Array.isArray(reports) ? reports : []) {
    const tg = tagOf(r && r.reportKey);
    if (!seen.includes(tg)) seen.push(tg);
  }
  return seen;
}

// 按标签过滤。tag 为 null／undefined ⇒ 全部；'' ⇒ 只看「其它」。
// **带日期的族最近的在最前**：一族的 key 都以 YYYY-MM-DD 结尾时（进度报告正是这一形），读者要的是
// 「最新一份」而不是字典序——这是那一族自身的显示语义，不是对后端顺序的第二种猜测；混着不带日期的 key
// 时不动顺序。
export function filterByTag(reports, tag) {
  const list = Array.isArray(reports) ? reports : [];
  if (tag === null || tag === undefined) return list;
  const picked = list.filter((r) => tagOf(r && r.reportKey) === tag);
  const dated = picked.map((r) => /(\d{4}-\d{2}-\d{2})$/.exec(r.reportKey));
  if (picked.length > 0 && dated.every(Boolean)) {
    return picked.map((r, i) => ({ r, d: dated[i][1] })).sort((a, b) => (a.d < b.d ? 1 : a.d > b.d ? -1 : 0)).map((x) => x.r);
  }
  return picked;
}

// 单篇详情里的修订台账 → 受控形状，最近的在最前。
// revision 相同（理论上不该发生，但这份数据来自另一个进程）时按时间兜底，保证
// 顺序是**全序**：不稳定的排序会让同一份数据在两次渲染里换位置。
export function normalizeRevisions(detail) {
  const rows = Array.isArray(detail?.revisions) ? detail.revisions : [];
  return rows
    .filter((h) => h && typeof h === 'object')
    .map((h) => ({
      revision: Math.max(0, Math.trunc(Number(h.revision)) || 0),
      atMs: Number.isFinite(Number(h.atMs)) ? Number(h.atMs) : 0,
      viaAgent: h.viaAgent === true,
      title: typeof h.title === 'string' ? h.title : '',
    }))
    .sort((a, b) => (b.revision - a.revision) || (b.atMs - a.atMs));
}
